import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import jwt_decode from 'jwt-decode'
import { thunks as AuthThunks } from 'reducers/Auth'
import { Modal, Button, Tonality, Pill } from '@dsny/dsny-component-library'
import {
  Close,
  Warning,
  Personadd,
  Delete,
  Arrowback
} from '@dsny/dsny-component-library/icons'
import { getAvatarInfo } from 'utils/avatarHelpers'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import ModalActions from 'reducers/Modal'
import { trackEvent } from 'utils/segmentService'
import {
  NetworkContainer,
  InviteIconContainer,
  InviteIconBg,
  InviteWrapper,
  ConnectionWrapper,
  NameWrapper,
  NameContainer,
  ContactContainer,
  IconWrapper,
  HeaderWrapper,
  XButtonWrapper,
  ModalWrapper,
  ModalContainer,
  ModalTitleWrapper,
  ButtonWrapper,
  DeleteIconContainer,
  StyledConnectionsColumn,
  ContentWrapper,
  InviteRow
} from './MyNetwork.styles'
import { ellipsis } from '../../styles/utils'

const NetworkConnection = ({ style = {} }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const token = localStorage.getItem('accessToken')
  const currentUser = jwt_decode(token)
  const verifiedUser = currentUser.verified

  const [deleteRecord, setDeleteRecord] = useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState(null)
  const [selectedRecordName, setSelectedRecordName] = useState('')

  const connections = useSelector((state) => state.authentication?.connections)

  const handleDeleteClick = (id, name) => {
    setSelectedRecordId(id)
    setSelectedRecordName(name)
    setDeleteRecord(true)
  }

  const handleDelete = async () => {
    if (selectedRecordId) {
      try {
        await dispatch(AuthThunks.deleteConnection(selectedRecordId))
        dispatch(AuthThunks.getConnections())
      } catch (error) {
        console.error('Error deleting connection:', error)
      }
      setDeleteRecord(false)
      setSelectedRecordId(null)
    }
  }

  const handleCancel = () => {
    setDeleteRecord(false)
  }

  const toggleInviteModal = () => {
    dispatch(ModalActions.toggleInviteModal())
  }

  const isConnectionsEmpty = connections?.length === 0 || connections === null

  const handleBack = () => {
    navigate('/account')
  }

  return (
    <>
      <NetworkContainer
        $isMobile={isMobile}
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        data-responsive-breakpoints={responsiveBreakpointsVersion}
        style={style}>
        <InviteWrapper $verifiedUser={verifiedUser} $isMobile={isMobile}>
          <Tonality.UIHeading
            headingStyle="headline5"
            color={theme.colors.neutralW100}
            style={{
              margin: isMobile ? '8px 0' : '0 0 12px 0',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {isMobile && (
              <Arrowback
                width={20}
                height={20}
                color={theme.colors.neutralW100}
                style={{ marginRight: 10 }}
                onClick={handleBack}
              />
            )}
            {t('network.my-network')}
          </Tonality.UIHeading>
          <Tonality.UIText
            textStyle="label"
            color={theme.colors.neutralW100}
            style={{
              marginTop: 6
            }}
          >
            {t('network.network-description')}
            {!verifiedUser && <i> {t('network.only-for-verified-users')}</i>}
          </Tonality.UIText>
        </InviteWrapper>

        <ContentWrapper $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {verifiedUser && (
            <>
              <InviteRow
                style={{
                  padding: isMobile ? '0' : '32px',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: isMobile ? 'center' : 'space-between',
                  backgroundColor: theme.colors.neutralW20,
                  border: isMobile
                    ? `1px solid ${theme.colors.neutralW50}`
                    : 'none',
                  borderRadius: isMobile ? '8px' : '5px',
                  display: 'block',
                  margin: 0
                }}
              >
                <InviteIconContainer $isMobile={isMobile}>
                  <InviteIconBg $isMobile={isMobile}>
                    <Personadd
                      width={isMobile ? '16px' : '16px'}
                      height={isMobile ? '16px' : '16px'}
                      color={theme.colors.neutralW20}
                    />
                  </InviteIconBg>
                  <Tonality.UIText
                    textStyle="labelBold"
                    color={theme.colors.neutralW90}
                  >
                    {t('network.invite-your-connection')}
                  </Tonality.UIText>
                  <Tonality.UIText
                    textStyle="caption"
                    color={theme.colors.neutralW90}
                    style={{
                      marginBottom: isMobile ? '12px' : 0,
                      textAlign: isMobile ? 'center' : 'left'
                    }}
                  >
                    {isMobile
                      ? t('network.invite-desc-mobile')
                      : t('invite-connection.invite-connection-desc')}
                  </Tonality.UIText>

                  <Pill
                    variant="outlined"
                    mode="dark"
                    label={t('invite-connection.invite-a-connection')}
                    iconPosition="leading"
                    icon={
                      <Personadd width={14} color={theme.colors.neutralW90} />
                    }
                    style={{ marginLeft: isMobile ? 'unset' : 'auto' }}
                    onClick={toggleInviteModal}
                  />
                </InviteIconContainer>
              </InviteRow>

              <Tonality.UIHeading
                headingStyle="headline6"
                color={theme.colors.neutralW100}
                style={{
                  lineHeight: '1.5rem',
                  margin: '24px 0',
                  display: 'flex'
                }}
              >
                {t('network.list-of-connections')}
              </Tonality.UIHeading>

              {connections?.length > 0 && (
                <StyledConnectionsColumn
                  $responsiveBreakpointsV2={responsiveBreakpointsV2}
                  style={{ padding: isMobile ? '0' : '32px' }}
                >
                  <HeaderWrapper $isMobile={isMobile}>
                    {!isMobile && (
                      <>
                        <Tonality.UIHeading
                          headingStyle="headline6"
                          color={theme.colors.neutralW100}
                        >
                          {t('network.name')}
                        </Tonality.UIHeading>
                        <Tonality.UIHeading
                          headingStyle="headline6"
                          color={theme.colors.neutralW100}
                        >
                          {t('network.email')}
                        </Tonality.UIHeading>
                      </>
                    )}
                  </HeaderWrapper>

                  <ConnectionWrapper $isMobile={isMobile}>
                    {connections?.map((connection, index) => {
                      const { initials, color } = getAvatarInfo(
                        connection.first_name,
                        connection.last_name,
                        connection.email
                      )

                      return (
                        <ContactContainer key={index} $isMobile={isMobile}>
                          {isMobile ? (
                            <>
                              <NameWrapper>
                                <Tonality.Avatar color={color}>
                                  {initials}
                                </Tonality.Avatar>
                                <NameContainer>
                                  <Tonality.UIText
                                    textStyle="bodyBold"
                                    color={theme.colors.neutralW60}
                                    style={{
                                      ...ellipsis,
                                      fontSize: '14px'
                                    }}
                                  >
                                    {connection?.full_name}
                                  </Tonality.UIText>

                                  {!connection?.verified && (
                                    <Tonality.UIText
                                      textStyle="body"
                                      color={theme.colors.neutralW50}
                                      style={{
                                        fontStyle: 'italic',
                                        fontSize: '14px'
                                      }}
                                    >
                                      {`(${t('network.verification-pending')})`}
                                    </Tonality.UIText>
                                  )}

                                  <Tonality.UIText
                                    textStyle="body"
                                    color={theme.colors.neutralW60}
                                    style={{
                                      ...ellipsis,
                                      fontSize: '12px'
                                    }}
                                  >
                                    {connection?.email}
                                  </Tonality.UIText>
                                </NameContainer>
                              </NameWrapper>
                            </>
                          ) : (
                            <>
                              <NameWrapper>
                                <Tonality.Avatar color={color}>
                                  {initials}
                                </Tonality.Avatar>
                                <Tonality.UIText
                                  textStyle="body"
                                  color={theme.colors.neutralW60}
                                  style={{
                                    ...ellipsis,
                                    maxWidth: '220px'
                                  }}
                                >
                                  {connection?.full_name}
                                </Tonality.UIText>

                                {!connection?.verified && (
                                  <Tonality.UIText
                                    textStyle="body"
                                    color={theme.colors.neutralW50}
                                    style={{ fontStyle: 'italic' }}
                                  >
                                    {`(${t('network.verification-pending')})`}
                                  </Tonality.UIText>
                                )}
                              </NameWrapper>

                              <Tonality.UIText
                                textStyle="body"
                                color={theme.colors.neutralW60}
                                style={{
                                  ...ellipsis
                                }}
                              >
                                {connection?.email}
                              </Tonality.UIText>
                            </>
                          )}

                          <IconWrapper>
                            <Tonality.Tooltip
                              mode="dark"
                              content={t('network.delete-connection')}
                              containerStyles={{ padding: '4px 8px' }}
                            >
                              <DeleteIconContainer
                                onClick={() =>
                                  handleDeleteClick(
                                    connection?.player_id,
                                    connection?.full_name
                                  )
                                }
                              >
                                <Delete
                                  width={isMobile ? '16px' : '18px'}
                                  height={isMobile ? '16px' : '18px'}
                                  style={{ cursor: 'pointer' }}
                                  color={theme.colors.neutralW90}
                                />
                              </DeleteIconContainer>
                            </Tonality.Tooltip>
                          </IconWrapper>
                        </ContactContainer>
                      )
                    })}
                  </ConnectionWrapper>
                </StyledConnectionsColumn>
              )}
            </>
          )}
        </ContentWrapper>

        {isConnectionsEmpty && (
          <Tonality.UIText textStyle="label" color={theme.colors.neutralW100}>
            {t('network.no-connections')}
          </Tonality.UIText>
        )}
      </NetworkContainer>

      <Modal
        isOpen={deleteRecord}
        onClose={handleCancel}
        analyticsModalType="network_delete_connection"
        trackEvent={trackEvent}
        containerStyles={{
          padding: isMobile ? '24px' : '32px 32px',
          background: theme.colors.neutralW20,
          border: `1px solid ${theme.colors.neutralW30}`,
          width: isMobile ? '100%' : 676,
          ...(isMobile
            ? { borderTop: `8px solid ${theme.colors.alert50}` } // Top border for mobile
            : { borderLeft: `8px solid ${theme.colors.alert50}` })
        }}
      >
        <XButtonWrapper onClick={handleCancel}>
          <Close width={24} color={theme.colors.neutralW80} />
        </XButtonWrapper>

        <ModalWrapper $isMobile={isMobile}>
          <ModalContainer $isMobile={isMobile}>
            <Warning width={24} color={theme.colors.alert50} />
            <ModalTitleWrapper $isMobile={isMobile}>
              <Tonality.UIHeading
                headingStyle="headline6"
                color={theme.colors.neutralW100}
                style={{ marginBottom: 8, lineHeight: '1.5rem' }}
              >
                {`${t('network.delete-banner-title')} ${selectedRecordName} ${t(
                  'network.delete-banner-title2'
                )}`}
              </Tonality.UIHeading>
              <Tonality.UIText
                textStyle="label"
                color={theme.colors.neutralW100}
              >
                {`${t('network.delete-banner-desc')} ${selectedRecordName} ${t(
                  'network.delete-banner-desc2'
                )}`}
              </Tonality.UIText>
            </ModalTitleWrapper>
          </ModalContainer>

          <ButtonWrapper $isMobile={isMobile}>
            <Button
              variant="secondary"
              setting="player"
              mode="dark"
              onClick={handleCancel}
            >
              {t('network.cancel')}
            </Button>
            <Button
              variant="primary"
              setting="player"
              mode="dark"
              onClick={handleDelete}
            >
              {t('network.yes-delete')}
            </Button>
          </ButtonWrapper>
        </ModalWrapper>
      </Modal>
    </>
  )
}

NetworkConnection.propTypes = {
  style: PropTypes.object
}

export default NetworkConnection
